import {
  Fragment,
  ReactElement,
  ReactNode,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { t } from "@lingui/core/macro";
import { useMutation } from "@tanstack/react-query";

import { Button } from "_/components/button";
import { PermissionTest } from "_/components/permission-test";
import { useSearchParams } from "_/components/router";
import { Skeleton } from "_/components/skeleton";
import { Table } from "_/components/table";

import { type Attempt } from "_/data/projects";
import { permissions } from "_/data/rbac";
import { isTelemetryNotReady, useAttemptTelemetryFile } from "_/data/telemetry";
import {
  type TelemetryColumn,
  type TelemetryKind,
  type TelemetryQueryResult,
  useTelemetryDatabase,
} from "_/data/telemetry/duckdb";

import { downloadCsv, toCsv } from "./csv";
import { resultColumns } from "./format";
import { PAGE_SIZE, Pagination, pageCount } from "./pagination";

import * as S from "./styled";

const DEFAULT_QUERY = "SELECT * FROM telemetry LIMIT 100";

/**
 * SQL console over the telemetry recorded during one print attempt.
 *
 * The attempt's telemetry is exposed as a single view named `telemetry`, so the
 * number of underlying files is never surfaced.
 */
export const TelemetryConsole = ({
  attempt,
}: {
  attempt: Attempt;
}): ReactElement => (
  <PermissionTest
    requiredPermissions={permissions.telemetry.read}
    fallback={<S.Notice>{t`components.telemetry-console.forbidden`}</S.Notice>}
  >
    <ConsoleContent attempt={attempt} />
  </PermissionTest>
);

const ConsoleContent = ({ attempt }: { attempt: Attempt }): ReactNode => {
  const fileResult = useAttemptTelemetryFile(attempt.id);
  const telemetry = fileResult.data;
  const file = telemetry?.state === "ready" ? telemetry.file : undefined;
  const database = useTelemetryDatabase(file);

  if (fileResult.isLoading) return <Skeleton variant="block" />;

  if (fileResult.isError) {
    // A print that has not finished is an expected state, not a failure.
    const message = isTelemetryNotReady(fileResult.error)
      ? t`components.telemetry-console.not-finished`
      : t`components.telemetry-console.manifest-error`;

    return <S.Notice>{message}</S.Notice>;
  }

  if (telemetry?.state === "generating") {
    return <S.Notice>{t`components.telemetry-console.preparing`}</S.Notice>;
  }

  if (!file) {
    return <S.Notice>{t`components.telemetry-console.no-telemetry`}</S.Notice>;
  }

  switch (database.status) {
    case "loading":
      return <S.Notice>{t`components.telemetry-console.preparing`}</S.Notice>;
    case "error":
      // Refetching changes the object identity the database effect keys on,
      // which rebuilds it from scratch.
      return (
        <S.Failure>
          <S.ErrorMessage>{database.error.message}</S.ErrorMessage>
          <Button size="small" onClick={() => void fileResult.refetch()}>
            {t`components.telemetry-console.retry`}
          </Button>
        </S.Failure>
      );
    case "ready":
      return (
        <QueryRunner
          attemptId={attempt.id}
          viewColumns={database.viewColumns}
          viewKinds={database.viewKinds}
          onRun={database.query}
        />
      );
    default: {
      // Every status is handled above, so adding one is a type error here.
      const unreachable: never = database;
      return unreachable;
    }
  }
};

type QueryRunnerProps = {
  attemptId: string;
  viewColumns: TelemetryColumn[];
  viewKinds: TelemetryKind[];
  onRun: (sql: string) => Promise<TelemetryQueryResult>;
};

function QueryRunner({
  attemptId,
  viewColumns,
  viewKinds,
  onRun,
}: QueryRunnerProps): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();

  const sharedQueryParam = searchParams.get("q");
  const sharedQuery = sharedQueryParam ? atob(sharedQueryParam) : null;
  const [sql, setSql] = useState(
    sharedQuery?.trim() ? sharedQuery : DEFAULT_QUERY
  );

  // react-query clears a mutation's `data` on `pending` and `error`, so the
  // last result is held separately to keep its rows on screen beside an error.
  const [result, setResult] = useState<TelemetryQueryResult>();

  const [page, setPage] = useState(1);

  const run = useMutation({
    mutationFn: onRun,
    onSuccess: (queryResult) => {
      setResult(queryResult);

      // Page 40 of the previous result very likely does not exist in this one.
      setPage(1);
    },
  });

  const total = result?.rows.length ?? 0;
  const pages = pageCount(total);
  const pageStart = (page - 1) * PAGE_SIZE;
  const rows = result?.rows.slice(pageStart, pageStart + PAGE_SIZE) ?? [];

  const firstRow = pageStart + 1;
  const lastRow = pageStart + rows.length;

  const submit = () => {
    // Recorded on run, not on keystroke, so the URL always describes a query
    // that was executed. `replace` keeps runs out of the history stack, so Back
    // still means "the previous page".
    const next = new URLSearchParams(searchParams);
    next.set("q", btoa(sql));
    setSearchParams(next, { replace: true });

    run.mutate(sql);
  };

  const autoRan = useRef(false);

  // Runs a shared link's query once, so it lands on results. `run` is a fresh
  // object each render, so the ref guard rather than a dependency array is what
  // limits it to one run.
  useEffect(() => {
    if (autoRan.current) return;
    autoRan.current = true;

    if (sharedQuery?.trim()) submit();
  });

  const exportCsv = () => {
    if (!result) return;

    downloadCsv(
      toCsv(result.columns, result.rows),
      `telemetry-${attemptId}.csv`
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Mirrors the Run button's `disabled` state: without it, two quick presses
    // run overlapping queries and the last to resolve wins, which can leave a
    // superseded query's rows on screen.
    if (run.isPending) return;

    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <S.Wrapper>
      <S.Editor
        value={sql}
        spellCheck={false}
        aria-label={t`components.telemetry-console.query-label`}
        onChange={(event) => setSql(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      <S.Toolbar>
        <Button size="small" disabled={run.isPending} onClick={submit}>
          {t`components.telemetry-console.run`}
        </Button>
        <Button
          kind="secondary"
          size="small"
          disabled={!result}
          onClick={exportCsv}
        >
          {t`components.telemetry-console.export-csv`}
        </Button>
        <S.Disclosure>
          <S.DisclosureSummary>
            {t`components.telemetry-console.columns`}
          </S.DisclosureSummary>
          <S.DisclosureList>
            {viewColumns.map((column) => (
              <Fragment key={column.name}>
                <dt>{column.name}</dt>
                <dd>{column.type}</dd>
              </Fragment>
            ))}
          </S.DisclosureList>
        </S.Disclosure>
        {viewKinds.length > 0 && (
          <S.Disclosure>
            <S.DisclosureSummary>
              {t`components.telemetry-console.kinds`}
            </S.DisclosureSummary>
            <S.DisclosureList>
              {viewKinds.map((entry) => (
                <Fragment key={`${entry.kind}-${entry.valueType}`}>
                  <dt>{entry.kind}</dt>
                  <dd>{entry.valueType}</dd>
                </Fragment>
              ))}
            </S.DisclosureList>
          </S.Disclosure>
        )}
      </S.Toolbar>
      {run.isError && <S.ErrorMessage>{run.error.message}</S.ErrorMessage>}
      {total > 0 && (
        <S.ResultHeader>
          <S.Notice>{t`components.telemetry-console.row-range ${firstRow} ${lastRow} ${total}`}</S.Notice>
          {pages > 1 && (
            <Pagination page={page} pages={pages} onSelect={setPage} />
          )}
        </S.ResultHeader>
      )}
      <Table
        columns={resultColumns(result?.columns ?? [])}
        data={rows}
        loading={run.isPending}
        noDataLabel={t`components.telemetry-console.no-results`}
        freezeHeaders={true}
      />
    </S.Wrapper>
  );
}
